import type { FC } from 'react';
import React, { Fragment, useMemo, useEffect, useContext, memo } from 'react';
import { css } from '@compiled/react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { EOP_RECS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useThirdPartyPromptState } from '@confluence/experiment-related-content-connections/entry-points/useThirdPartyPromptState.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import {
	SetSSRPartialSuccess,
	SSR_PARTIAL_COMPONENT,
} from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess.ts';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { ViewPagePersonalizationReader } from '@confluence/content-unified-query/entry-points/ViewPagePersonalizationReader.ts';
import { buildViewPagePersonalizationVariables } from '@confluence/content-unified-query/entry-points/viewPagePersonalizationVariables.ts';
import { type ViewPagePersonalizationQuery$data } from '@confluence/content-unified-query/entry-points/ViewPagePersonalizationQuery.ts';

import type { EndOfPageRecommendation_recommendedPagesReader$key } from './__generated__/EndOfPageRecommendation_recommendedPagesReader.graphql.ts';
import type { EndOfPageRecommendation_spaceStatusReader$key } from './__generated__/EndOfPageRecommendation_spaceStatusReader.graphql.ts';

import { END_OF_PAGE_RECOMMENDATIONS_METRIC } from './perf.config.ts';
import EndOfPageRecommendationQuery from './__generated__/EndOfPageRecommendationQuery.graphql.ts';
import { transformAndOrderRecs } from './transformers.ts';
import {
	RecommendedPagesSpaceBehavior,
	type EndOfPageRecommendationQuery as EndOfPageRecommendationData,
	type EndOfPageRecommendationQueryVariables,
	type EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages,
} from './__types__/EndOfPageRecommendationQuery.ts';
import { EndOfPageRecPlaceholderContainer } from './SharedComponents.tsx';
import { EndOfPageRecommendationComponent } from './EndOfPageRecommendationComponent.tsx';

// PersonalizationContract fragment baseline. Colocated here so the end-of-page recommendation fields
// are owned by their consumer, then spread by whichever operation supplies them. Both are spread
// unmasked from EndOfPageRecommendationQuery because that operation is still read as `$data` through
// interop, by this component via `./__types__/EndOfPageRecommendationQuery.ts` and by `content-tools`
// as a refetch target. See confluence/openspec/changes/personalization-contract.
export const EndOfPageRecommendationRecommendedPagesFragment: GraphQLTaggedNode = graphql`
	fragment EndOfPageRecommendation_recommendedPages on RecommendedPages {
		recommendedPages {
			content {
				id
				title
				type
				subType
				creatorId
				links {
					webui
				}
				history {
					ownedBy {
						displayName
					}
				}
				space {
					id
					key
					name
				}
				emoji: properties(key: "emoji-title-published") {
					nodes {
						id
						key
						value
					}
				}
			}
			strategy
		}
	}
`;

export const EndOfPageRecommendationSpaceStatusFragment: GraphQLTaggedNode = graphql`
	fragment EndOfPageRecommendation_spaceStatus on RecommendedPagesSpaceStatus {
		recommendedPagesEnabled
		defaultBehavior
	}
`;

const endOfPageRecWrapperStyles = css({
	marginTop: token('space.1000'),
});

export type EndOfPageRecommendationProps = {
	entityId: string;
	entityType: string;
	pageWidthType: string;
	spaceKey: string;
	isLivePage?: boolean;
};

const EndOfPageRecommendation: FC<
	EndOfPageRecommendationProps & { personalizationData?: EndOfPageRecommendationData }
> = memo(
	({ entityType, entityId, pageWidthType, spaceKey, isLivePage = false, personalizationData }) => {
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
		const [state] = useContentState();
		const skip = entityType !== 'page' && entityType !== 'blogpost';
		const experienceTracker = useContext(ExperienceTrackerContext);

		const baseQueryOptions = {
			variables: {
				type: entityType,
				id: entityId,
				exp: expVal(
					'cc_ai_discovery_linked_pages_on_related_pages',
					'experience',
					'end_of_page_v5',
				),
				key: spaceKey,
				keyString: spaceKey,
			},
			context: { single: true },
			errorPolicy: 'all' as const,
			skip: skip || personalizationData !== undefined,
		};

		const legacy = useQueryNoFurtherUpdateInterop<
			EndOfPageRecommendationData,
			EndOfPageRecommendationQueryVariables
		>(EndOfPageRecommendationQuery, baseQueryOptions);
		const { error, loading, data } =
			personalizationData === undefined
				? legacy
				: { error: undefined, loading: false, data: personalizationData };
		const { state: promptState } = useThirdPartyPromptState(entityId);
		const isGridLoading = loading || promptState.kind === 'pending';

		const pageCardData = useMemo(
			() =>
				transformAndOrderRecs(
					data?.getRecommendedPages
						?.recommendedPages as EndOfPageRecommendationQuery_getRecommendedPages_recommendedPages[],
				),
			[data],
		);

		const isEnabled =
			data?.getRecommendedPagesSpaceStatus?.recommendedPagesEnabled &&
			data?.getRecommendedPagesSpaceStatus?.defaultBehavior === RecommendedPagesSpaceBehavior.SHOWN;

		useEffect(() => {
			if (isEnabled) {
				experienceTracker.start({
					name: EOP_RECS_EXPERIENCE,
				});
			}
		}, [experienceTracker, isEnabled]);

		useEffect(() => {
			if (isEnabled && !loading) {
				if (error) {
					experienceTracker.stopOnError({
						name: EOP_RECS_EXPERIENCE,
						error,
					});
				} else {
					experienceTracker.succeed({
						name: EOP_RECS_EXPERIENCE,
					});
				}
			}
		}, [experienceTracker, isEnabled, loading, error]);

		if (error) {
			return isUnauthorizedError(error) ? null : <ErrorDisplay error={error} />;
		}

		if (skip || !pageCardData || pageCardData.length < 2 || !isEnabled) {
			return <SetSSRPartialSuccess name={SSR_PARTIAL_COMPONENT.EndOfPageRecommendation} />;
		}

		const isContentReady =
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR) ||
			window.__SSR_RENDERED__ ||
			state.isContentReady ||
			isLivePage;

		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="end-of-page-rec-wrapper"
				data-testid="end-page-rec-wrapper"
				data-vc="end-of-page-recommendation"
				css={isLivePage && endOfPageRecWrapperStyles}
				{...ssrPlaceholderIdProp}
			>
				{isEnabled &&
					isContentReady &&
					(isGridLoading ? (
						<UFOLoadHold name="EndOfPageRecommendation">
							<EndOfPageRecPlaceholderContainer />
						</UFOLoadHold>
					) : (
						<Fragment>
							<PageSegmentLoadStart
								key={entityId}
								metric={END_OF_PAGE_RECOMMENDATIONS_METRIC}
								isCustomStart
							/>
							<EndOfPageRecommendationComponent
								entityId={entityId}
								entityType={entityType}
								pageCardData={pageCardData}
								data={data}
								pageWidthType={pageWidthType}
								isLivePage={isLivePage}
							/>
							<SetSSRPartialSuccess name={SSR_PARTIAL_COMPONENT.EndOfPageRecommendation} />
						</Fragment>
					))}
			</div>
		);
	},
);

const RecommendationFragmentReader = ({
	data,
	...props
}: EndOfPageRecommendationProps & {
	data: ViewPagePersonalizationQuery$data;
}): React.JSX.Element => {
	const recommendedPages = useFragment<EndOfPageRecommendation_recommendedPagesReader$key>(
		EndOfPageRecommendationRecommendedPagesFragmentReader,
		data.getRecommendedPages ?? null,
	);
	const spaceStatus = useFragment<EndOfPageRecommendation_spaceStatusReader$key>(
		EndOfPageRecommendationSpaceStatusFragmentReader,
		data.getRecommendedPagesSpaceStatus ?? null,
	);
	return (
		<EndOfPageRecommendation
			{...props}
			personalizationData={{
				getRecommendedPages: recommendedPages ?? null,
				getRecommendedPagesSpaceStatus: spaceStatus ?? null,
				space: data.space ?? null,
			}}
		/>
	);
};

const RecommendationPersonalization = (props: EndOfPageRecommendationProps): React.JSX.Element => {
	const { cloudId, isLoggedIn } = useSessionData();
	const fallback = <EndOfPageRecommendation {...props} />;
	if (
		!isLoggedIn ||
		props.isLivePage ||
		!props.spaceKey ||
		(props.entityType !== 'page' && props.entityType !== 'blogpost') ||
		StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_recs_reader') ||
		UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true) ||
		!expVal('confluence_exp_personalization_contract', 'isEnabled', false)
	)
		return fallback;
	const variables = buildViewPagePersonalizationVariables(
		{
			cloudId,
			contentId: props.entityId,
			spaceKey: props.spaceKey,
			isBlog: props.entityType === 'blogpost',
		},
		{
			recommendations: {
				experience: expVal(
					'cc_ai_discovery_linked_pages_on_related_pages',
					'experience',
					'end_of_page_v5',
				),
			},
		},
	);
	return (
		<ViewPagePersonalizationReader variables={variables} fallback={fallback}>
			{(data) =>
				data.getRecommendedPages === undefined ||
				data.getRecommendedPagesSpaceStatus === undefined ||
				data.space === undefined ? (
					fallback
				) : (
					<RecommendationFragmentReader {...props} data={data} />
				)
			}
		</ViewPagePersonalizationReader>
	);
};

const EndOfPageRecommendationWithErrorBoundary = (
	props: EndOfPageRecommendationProps,
): React.JSX.Element => {
	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<RecommendationPersonalization {...props} />
		</ErrorBoundary>
	);
};

export { EndOfPageRecommendationWithErrorBoundary as EndOfPageRecommendation };

export const EndOfPageRecommendationRecommendedPagesFragmentReader: GraphQLTaggedNode = graphql`
	fragment EndOfPageRecommendation_recommendedPagesReader on RecommendedPages @throwOnFieldError {
		...EndOfPageRecommendation_recommendedPages @relay(mask: false)
	}
`;

export const EndOfPageRecommendationSpaceStatusFragmentReader: GraphQLTaggedNode = graphql`
	fragment EndOfPageRecommendation_spaceStatusReader on RecommendedPagesSpaceStatus
	@throwOnFieldError {
		...EndOfPageRecommendation_spaceStatus @relay(mask: false)
	}
`;
